package one.healthium.db.database

import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass
import one.healthium.db.AppDatabase
import one.healthium.db.DailyTasksQueries
import one.healthium.db.FoodQueries
import one.healthium.db.SavedContentQueries
import one.healthium.db.ShoppingListDataQueries

internal val KClass<AppDatabase>.schema: SqlSchema<QueryResult.AsyncValue<Unit>>
  get() = AppDatabaseImpl.Schema

internal fun KClass<AppDatabase>.newInstance(driver: SqlDriver): AppDatabase = AppDatabaseImpl(driver)

private class AppDatabaseImpl(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver),
    AppDatabase {
  override val dailyTasksQueries: DailyTasksQueries = DailyTasksQueries(driver)

  override val foodQueries: FoodQueries = FoodQueries(driver)

  override val savedContentQueries: SavedContentQueries = SavedContentQueries(driver)

  override val shoppingListDataQueries: ShoppingListDataQueries = ShoppingListDataQueries(driver)

  public object Schema : SqlSchema<QueryResult.AsyncValue<Unit>> {
    override val version: Long
      get() = 7

    override fun create(driver: SqlDriver): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS dailyTaskEntity (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    date TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    type TEXT NOT NULL,
          |    color INTEGER NOT NULL,
          |    payload TEXT,
          |    completionCount INTEGER NOT NULL DEFAULT 0
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE FoodEntity (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT NOT NULL,
          |    product TEXT NOT NULL,
          |    emoji TEXT NOT NULL,
          |    description TEXT NOT NULL,
          |    calories REAL NOT NULL,
          |    fat REAL NOT NULL,
          |    carbohydrates REAL NOT NULL,
          |    fiber REAL NOT NULL,
          |    protein REAL NOT NULL,
          |    tablespoon REAL,
          |    cups REAL,
          |    pieces REAL,
          |    isLiquid INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS savedContentEntity (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    contentId TEXT NOT NULL,
          |    contentType TEXT NOT NULL,
          |    title TEXT,
          |    description TEXT,
          |    thumbUrl TEXT,
          |    saveType TEXT NOT NULL,
          |    savedAt INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS shoppingListDataEntity (
          |    id TEXT PRIMARY KEY NOT NULL,
          |    listId TEXT NOT NULL,
          |    itemId TEXT NOT NULL,
          |    checked INTEGER NOT NULL DEFAULT 1
          |)
          """.trimMargin(), 0).await()
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      if (oldVersion <= 1 && newVersion > 1) {
        driver.execute(null, """
            |CREATE TABLE FoodSuggestionEntity (
            |    id INTEGER PRIMARY KEY AUTOINCREMENT NOT NULL,
            |    name TEXT NOT NULL,
            |    emoji TEXT NOT NULL
            |)
            """.trimMargin(), 0).await()
      }
      if (oldVersion <= 2 && newVersion > 2) {
        driver.execute(null, """
            |CREATE TABLE FoodEntity (
            |    id INTEGER PRIMARY KEY AUTOINCREMENT NOT NULL,
            |    product TEXT NOT NULL,
            |    emoji TEXT NOT NULL,
            |    description TEXT NOT NULL,
            |    calories REAL NOT NULL,
            |    fat REAL NOT NULL,
            |    carbohydrates REAL NOT NULL,
            |    fiber REAL NOT NULL,
            |    protein REAL NOT NULL
            |)
            """.trimMargin(), 0).await()
      }
      if (oldVersion <= 4 && newVersion > 4) {
        driver.execute(null, """
            |CREATE TABLE IF NOT EXISTS shoppingListDataEntity (
            |    id INTEGER PRIMARY KEY AUTOINCREMENT NOT NULL,
            |    listId TEXT NOT NULL,
            |    itemId TEXT NOT NULL,
            |    checked INTEGER NOT NULL DEFAULT 1
            |)
            """.trimMargin(), 0).await()
      }
      if (oldVersion <= 5 && newVersion > 5) {
        driver.execute(null, "ALTER TABLE FoodEntity ADD COLUMN tablespoon REAL", 0).await()
        driver.execute(null, "ALTER TABLE FoodEntity ADD COLUMN cups REAL", 0).await()
        driver.execute(null, "ALTER TABLE FoodEntity ADD COLUMN pieces REAL", 0).await()
        driver.execute(null, "ALTER TABLE FoodEntity ADD COLUMN isLiquid INTEGER NOT NULL DEFAULT 0", 0).await()
      }
      if (oldVersion <= 6 && newVersion > 6) {
        driver.execute(null, """
            |CREATE TABLE IF NOT EXISTS savedContentEntity (
            |    id TEXT PRIMARY KEY NOT NULL,
            |    contentId TEXT NOT NULL,
            |    contentType TEXT NOT NULL,
            |    title TEXT,
            |    description TEXT,
            |    thumbUrl TEXT,
            |    saveType TEXT NOT NULL,
            |    savedAt INTEGER NOT NULL
            |)
            """.trimMargin(), 0).await()
      }
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion + 1).await()
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion).await()
      }
    }
  }
}
